import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import {
  type ProductTypeSerializer,
  type ProductVariantSerializer,
} from "@app-contexts/public/features/products/data/types/products";

import { UserMembershipGroupDetailSerializer } from "@app-contexts/user/features/memberships/data/types";
import { enums } from "@shared/constants";
import { PublicInternalAnalyticsTrackingSerializer } from "@shared/modules/analytics/types";
import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import { TicketBasketType } from "@shared/modules/basket/types";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import { isRawBasket } from "@shared/modules/basket/utils/helpers";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import isEmpty from "lodash/isEmpty";
import { DataUtils } from "./DataUtils";
import { IInteractionDataBuilder } from "./IInteractionDataBuilder";

export type AddToCartDataBuilderData = {
  item: TicketItemGroupType;
  count?: number;
  basket: TicketBasketType | IBasket;
  venue?: string;
  event?: Partial<PublicBaseEventSerializer | VenueBasedEventSerializer>;
  product?: ProductTypeSerializer;
  productVariant?: ProductVariantSerializer;

  membershipGroup?: Partial<
    | PublicMembershipGroupSerializer
    | UserMembershipGroupDetailSerializer
    | VenueBasedMembershipGroupSerializer
  >;
};

/**
 * Builder for tracking when items are added to the cart.
 *
 * This builder handles multiple types of items that can be added:
 * - Tickets
 * - Products
 * - Memberships
 * - Donations
 *
 * It supports both single-item and bulk add-to-cart operations.
 * For bulk operations, it aggregates the items into a single analytics event.
 *
 * Business Logic:
 * - Different item types need different tracking parameters
 * - We track both the item details and the basket context
 * - For tickets, we track the associated event and ticket type
 * - For products, we track product and variant details
 * - For memberships, we track the membership level
 * - For donations, we track the donation amount
 */
export class AddToCartDataBuilder implements IInteractionDataBuilder {
  private data: AddToCartDataBuilderData | AddToCartDataBuilderData[];

  constructor(data: AddToCartDataBuilderData | AddToCartDataBuilderData[]) {
    if (Array.isArray(data)) {
      this.data = data.length > 1 ? data : data[0];
    } else {
      this.data = data;
    }
  }

  /**
   * Gets custom parameters specific to each item type.
   * This helps with:
   * - Segmenting analytics by item type
   * - Tracking type-specific metrics (e.g. ticket types, product variants)
   * - Supporting marketing attribution for different product types
   */
  getItemCustomParams = ({
    item,
    product,
    productVariant,

    membershipGroup,
  }: {
    item: TicketItemGroupType;
    product?: ProductTypeSerializer;
    productVariant?: ProductVariantSerializer;
    membershipGroup?: AddToCartDataBuilderData["membershipGroup"];
  }) => {
    switch (item.item_type) {
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id:
        return {
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
          item_id: item.event?.id,
          item_variant_id: item.type,
        };
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id:
        return {
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
          item_id: product?.id,
          item_variant_id: productVariant?.id,
        };
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id:
        return {
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
          item_variant_id:
            // eslint-disable-next-line
            // @ts-expect-error
            membershipGroup?.membership_levels?.find(
              (ml: { id: number }) => ml.id === item.type
            )?.id ??
            membershipGroup?.membership_levels?.[0]?.id ??
            item.type,
          item_id: membershipGroup?.id,
        };
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id:
        return {
          item_type: enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id,
          amount: item.donation_amount,
        };
      default:
        return {
          item_type: item.item_type,
        };
    }
  };

  singleItemBuilder = (): Omit<
    PublicInternalAnalyticsTrackingSerializer,
    "interaction_type"
  > => {
    // this should never happen since we decide which builder to use based on if data is an array or not.
    // this is just to satisfy typescript.
    if (Array.isArray(this.data)) {
      throw new Error("singleItemBuilder does not support multiple items");
    }
    const {
      item,
      count,
      venue,
      basket,
      event,
      product,
      productVariant,
      membershipGroup,
    } = this.data;

    const basketFields = isRawBasket(basket)
      ? {
          id: basket.id,
          payment_venue: basket.payment_venue,
          purchase_source_platform: basket.purchase_source_platform,
          permission_ids: DataUtils.getPermissionIdsFromItemGroups(
            basket.item_groups
          ),
        }
      : {
          id: basket.basketId,
          payment_venue: basket.paymentVenue,
          purchase_source_platform: basket.purchaseSourcePlatform,
          permission_ids: DataUtils.getPermissionIdsFromItemGroups(
            basket.itemGroups
          ),
        };

    let event_id: PublicInternalAnalyticsTrackingSerializer["event_id"];
    let ticket_type_id: PublicInternalAnalyticsTrackingSerializer["ticket_type_id"];
    let custom_params: Record<string, unknown> = {};
    switch (item.item_type) {
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id: {
        event_id = event?.id;
        ticket_type_id = item.type;
        custom_params = this.getItemCustomParams({ item });
        break;
      }
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id: {
        custom_params = this.getItemCustomParams({
          item,
          product,
          productVariant,
        });
        break;
      }
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id: {
        custom_params = this.getItemCustomParams({
          item,
          membershipGroup,
        });
        break;
      }
      case enums.ITEM_TYPES_MAP.ITEM_TYPE_DONATION.id: {
        custom_params = this.getItemCustomParams({
          item,
        });
        break;
      }
      case "protection": {
        custom_params = this.getItemCustomParams({
          item,
        });
        break;
      }
      default: {
        console.warn("No case"); //eslint-disable-line
      }
    }

    custom_params.count = count;
    custom_params.venue = venue;
    custom_params.purchase_source_platform =
      basketFields.purchase_source_platform;
    custom_params.permission_ids = basketFields.permission_ids;

    return {
      event_id,
      ticket_type_id,
      basket_id: basketFields.id,
      venue_id: basketFields.payment_venue,
      custom_params,
    };
  };

  multipleItemsBuilder = (): Omit<
    PublicInternalAnalyticsTrackingSerializer,
    "interaction_type"
  > => {
    // this should never happen since we decide which builder to use based on if data is an array or not.
    // this is just to satisfy typescript.
    if (!Array.isArray(this.data)) {
      throw new Error("multipleItemsBuilder does not support single item");
    }
    const { basket } = this.data[0];
    const builtCustomParams: Record<string, unknown> = {};

    const basketFields = isRawBasket(basket)
      ? {
          id: basket.id,
          payment_venue: basket.payment_venue,
          purchase_source_platform: basket.purchase_source_platform,
          permission_ids: DataUtils.getPermissionIdsFromItemGroups(
            basket.item_groups
          ),
        }
      : {
          id: basket.basketId,
          payment_venue: basket.paymentVenue,
          purchase_source_platform: basket.purchaseSourcePlatform,
          permission_ids: DataUtils.getPermissionIdsFromItemGroups(
            basket.itemGroups
          ),
        };

    this.data.forEach((item) => {
      const {
        item: itemGroup,
        count,
        venue,
        event,
        product,
        productVariant,
        membershipGroup,
      } = item;

      builtCustomParams[`${itemGroup.item_type}-${itemGroup.id}`] = {
        ...this.getItemCustomParams({
          item: { item_type: itemGroup.item_type, id: itemGroup.id },
          product,
          productVariant,
          membershipGroup,
        }),
        event_id: event?.id,
        ticket_type_id: itemGroup.type,
        count,
        venue,
      };
    });

    return {
      basket_id: basketFields.id,
      venue_id: basketFields.payment_venue,
      custom_params: {
        ...builtCustomParams,
        purchase_source_platform: basketFields.purchase_source_platform,
        permission_ids: basketFields.permission_ids,
      },
    };
  };

  build(): PublicInternalAnalyticsTrackingSerializer | null {
    if (!this.data || isEmpty(this.data)) {
      return null;
    }

    const data = Array.isArray(this.data)
      ? this.multipleItemsBuilder()
      : this.singleItemBuilder();
    return {
      ...data,
      interaction_type: enums.INTERACTION_TYPE_MAP.ADD_TO_CART.id,
      label: enums.INTERACTION_LABEL_MAP.ADD_TO_CART.id,
    };
  }
}
